import React from 'react';
import { fleetLocationStyles } from './fleetLocationStyles';

const styles = {
  editor: { display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, maxWidth: '100%', verticalAlign: 'middle' },
  button: { font: 'inherit', fontSize: 14, minHeight: 36, padding: '6px 10px', border: '1px solid #b0bec5', borderRadius: 4, background: '#fff', color: '#263238', cursor: 'pointer' },
  actionButton: { ...fleetLocationStyles.default.buttonSmall, minHeight: 36 },
  actionDisabled: { opacity: fleetLocationStyles.default.buttonDisabled.opacity, cursor: fleetLocationStyles.default.buttonDisabled.cursor },
  input: { font: 'inherit', fontSize: 16, minWidth: 0, width: 220, maxWidth: '100%', padding: '7px 8px', border: '1px solid #78909c', borderRadius: 4, boxSizing: 'border-box', color: '#ffffff', backgroundColor: 'transparent', WebkitTextFillColor: '#ffffff' },
  error: { flexBasis: '100%', fontSize: 14, color: '#FF8A80' },
};

// Presentational only: hosts own their one-field PATCH and local identity merge.
// Hosts key this component by entity and page generation, so a new scope drops the draft.
class InlineNameEditor extends React.Component {
  state = { editing: false, draft: '', pending: false, error: '' };
  pending = false;
  composing = false;
  mounted = false;
  controller = null;
  input = React.createRef();
  pencil = React.createRef();

  componentDidMount() { this.mounted = true; }

  componentWillUnmount() {
    this.mounted = false;
    if (this.controller) { this.controller.abort(); }
  }

  begin = () => {
    this.composing = false;
    this.setState({ editing: true, draft: this.props.value || '', error: '' }, () => this.input.current.focus());
  };

  cancel = () => {
    if (this.pending) { return; }
    this.setState({ editing: false, error: '' }, () => this.pencil.current.focus());
  };

  save = async () => {
    if (this.pending) { return; }
    const value = this.state.draft.trim();
    if (Array.from(value).length > 255) {
      this.setState({ error: 'nameTooLong' });
      return;
    }
    this.pending = true;
    this.controller = typeof AbortController === 'undefined' ? null : new AbortController();
    this.setState({ pending: true, error: '' });
    try {
      await this.props.onSave(value, this.controller && this.controller.signal);
      if (!this.mounted) { return; }
      this.pending = false;
      this.setState({ pending: false, editing: false }, () => this.pencil.current.focus());
    } catch (error) {
      if (!this.mounted) { return; }
      this.pending = false;
      this.setState({ pending: false, error: 'nameSaveFailed' });
    }
  };

  keyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!this.composing && !(event.nativeEvent && event.nativeEvent.isComposing)
        && event.keyCode !== 229) { this.save(); }
    }
  };

  render() {
    const { t, contextLabel, display } = this.props;
    const editLabel = t('editName', { name: contextLabel });
    const { editing, draft, pending, error } = this.state;
    const tooLong = Array.from(draft.trim()).length > 255;
    return (
      <span style={styles.editor} onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}>
        {!editing && (
          <React.Fragment>
            {display}
            <button type='button' ref={this.pencil} aria-label={editLabel}
              title={editLabel} style={{ ...styles.button, background: 'transparent', borderColor: 'transparent', color: '#fff' }} onClick={this.begin}>
              <span aria-hidden='true' className='pi pi-pencil' />
            </button>
          </React.Fragment>
        )}
        {editing && (
          <React.Fragment>
            <input ref={this.input} aria-label={t('nameInput')} value={draft} disabled={pending}
              style={styles.input} autoComplete='off' onKeyDown={this.keyDown}
              onCompositionStart={() => { this.composing = true; }}
              onCompositionEnd={() => { this.composing = false; }}
              onChange={(event) => this.setState({ draft: event.target.value, error: '' })} />
            <button type='button' aria-label={t('saveName')} disabled={pending || tooLong}
              style={pending || tooLong ? { ...styles.actionButton, ...styles.actionDisabled } : styles.actionButton} onClick={this.save}>{t('saveName')}</button>
            <button type='button' aria-label={t('cancel')} disabled={pending}
              style={pending ? { ...styles.actionButton, ...styles.actionDisabled } : styles.actionButton} onClick={this.cancel}>{t('cancel')}</button>
            {(tooLong || error) && <span role='alert' style={styles.error}>{t(tooLong ? 'nameTooLong' : error)}</span>}
          </React.Fragment>
        )}
      </span>
    );
  }
}

export default InlineNameEditor;
